import { useEffect, useMemo } from "react";

import { useNavigate } from "react-router-dom";
import { useBreakpoints, LoadingSkeleton } from "@narmi/design_system";
import { modules, entities } from "byzantine";

import { useSubscriptionCards } from "../../contexts/SubscriptionCardsContext";
import SubscriptionsChart from "../SubscriptionsChart";
import SubscriptionsTable from "../SubscriptionsTable";

interface SubscriptionSummaryProps {
  cardId: API.CardId;
}

const SubscriptionSummary = ({ cardId }: SubscriptionSummaryProps) => {
  const { s, m, l, xl } = useBreakpoints();
  const navigate = useNavigate();
  const screenSize = useMemo<
    keyof typeof modules.subscriptions.webConfigs
  >(() => {
    switch (true) {
      case xl:
        return "xl";
      case l:
        return "l";
      case m:
        return "m";
      case s:
        return "s";
      default:
        return "xs";
    }
  }, [s, m, l, xl]);
  const {
    data,
    monthLabels,
    selectedMonth,
    prevMonth,
    nextMonth,
    selectedMonthFullName,
    year,
    isSelectedThisMonth,
    prev12MonthsTotal,
    monthTotal,
    selectedTransactions,
    onMonthChange,
  } = modules.subscriptions.useSubscriptionsChartData(cardId, screenSize);
  const { send: sendSubscriptions, loading: isLoadingSubscriptions } =
    entities.subscriptions.useSubscriptionRequests();
  const { send: sendMerchants, loading: isLoadingMerchants } =
    entities.merchants.useFetchMerchantsRequest();
  const { cards, isLoading: isCardsLoading } = useSubscriptionCards();

  useEffect(() => {
    sendSubscriptions(cardId);
  }, [cardId, sendSubscriptions]);

  useEffect(() => {
    sendMerchants();
  }, [sendMerchants]);

  const handleRowClick = (subcription: API.CardTransaction) => {
    navigate(`/cards/${cardId}/subscriptions/${subcription.id}`);
  };

  const selectedCard = useMemo(
    () => cards.find((card) => card.id === cardId),
    [cards, cardId]
  );

  if (
    isLoadingSubscriptions ||
    isLoadingMerchants ||
    isCardsLoading ||
    !selectedTransactions
  ) {
    return <LoadingSkeleton isLoading />;
  }

  return (
    <>
      <SubscriptionsChart
        data={data}
        monthLabels={monthLabels}
        selectedMonth={selectedMonth}
        prevMonth={prevMonth}
        nextMonth={nextMonth}
        selectedMonthFullName={selectedMonthFullName}
        year={year}
        isSelectedThisMonth={isSelectedThisMonth}
        prev12MonthsTotal={prev12MonthsTotal}
        monthTotal={monthTotal}
        onMonthChange={onMonthChange}
        screenSize={screenSize}
      />
      <SubscriptionsTable
        cardName={selectedCard?.name ?? ""}
        transactions={selectedTransactions}
        onRowClick={handleRowClick}
      />
    </>
  );
};

export default SubscriptionSummary;
